import { ArrowLeftIcon, DeviceTabletIcon } from "@heroicons/react/outline";
import { TrashIcon } from "@heroicons/react/solid";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DeleteModal from "../../../../components/DeleteModal/DeleteModal";
import Sidebar from "../../../../components/Sidebar/Sidebar";
import { apiV3 } from "../../../../utils/helpers/api";
import { useToast } from "../../../../components/Toastr/Toastr";
import {
  APP_TYPES,
  locationHint,
  usesSelfServeLocation,
} from "../tabletAppTypes";

export default function EditTabletV3() {
  const { showSuccessToast, showErrorToast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [device, setDevice] = useState<any>(null);
  const [name, setName] = useState<string>("");
  const [appType, setAppType] = useState<string>("SelfServe");
  const [locationId, setLocationId] = useState<string>("");
  const [active, setActive] = useState<boolean>(true);
  const [selfServeLocations, setSelfServeLocations] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [openDeleteTabletModal, setOpenDeleteTabletModal] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const deviceId = params.deviceId;
    if (deviceId) {
      fetchTablet(deviceId);
      fetchLocationOptions();
    } else {
      showErrorToast();
    }
  }, []);

  const fetchTablet = async (deviceId: string) => {
    apiV3
      .getDeviceById(deviceId)
      .then((res) => {
        if (res.status === 200 && res.data) {
          const device = res.data;
          setDevice(device);
          setName(device.deviceIdentifier);
          setAppType(device.appType);
          setActive(device.active);
          setLocationId(device.selfServeLocation?.id ?? device.location?.id ?? "");
        }
      })
      .catch((error) => {
        console.error(`Error occurred while fetching tablet with id ${deviceId}`, error);
        showErrorToast();
      })
      .finally(() => setLoading(false));
  };

  const fetchLocationOptions = async () => {
    try {
      const [selfServeLocationsRes, locationsRes] = await Promise.all([
        apiV3.getSelfServeLocations(1, 100, "", "", "ASC", "name"),
        apiV3.getLocations(1, 100, "", "", "ASC", "name"),
      ]);
      setSelfServeLocations(selfServeLocationsRes.data.data);
      setLocations(locationsRes.data.data);
    } catch (error) {
      console.error("Error occurred while fetching location options - ", error);
      showErrorToast();
    }
  };

  const updateAppType = (newAppType: string) => {
    // The location lists differ per app type, so a previous choice is invalid
    setAppType(newAppType);
    setLocationId("");
  };

  const locationOptions = usesSelfServeLocation(appType) ? selfServeLocations : locations;

  const updateTablet = async () => {
    setIsBusy(true);
    try {
      if (params.deviceId) {
        await apiV3.updateDeviceById(params.deviceId, {
          deviceIdentifier: name,
          appType,
          active,
          ...(usesSelfServeLocation(appType)
            ? { selfServeLocationId: locationId, locationId: null }
            : { locationId, selfServeLocationId: null }),
        });
        showSuccessToast("Successfully updated tablet");
        await fetchTablet(params.deviceId);
      } else {
        showErrorToast();
      }
    } catch (error: any) {
      console.error("Error occurred while updating tablet - ", error);
      if (error?.response?.status === 409) {
        showErrorToast("A tablet with that name already exists");
      } else {
        showErrorToast();
      }
    }
    setIsBusy(false);
  };

  const deleteTablet = async () => {
    setIsBusy(true);
    setOpenDeleteTabletModal(false);
    try {
      if (params.deviceId) {
        await apiV3.deleteDeviceById(params.deviceId);
        showSuccessToast("Successfully deleted tablet");
        navigate("/v3/tablets");
      } else {
        showErrorToast();
      }
    } catch (error) {
      console.error("Error occurred while deleting tablet - ", error);
      showErrorToast();
      setIsBusy(false);
    }
  };

  return (
    <>
      <Sidebar />
      <DeleteModal
        open={openDeleteTabletModal}
        setOpen={setOpenDeleteTabletModal}
        action={deleteTablet}
        title="Delete Tablet"
        description="Are you sure you want to delete this tablet? It will no longer be able to retrieve its location and the app on it will stop working."
      />
      <div className="md:pl-64 flex flex-col flex-1">
        <main className="flex-1">
          <div className="py-4">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                  <h1 className="text-2xl font-bold text-gray-900 flex flex-row items-center">
                    <DeviceTabletIcon className="h-6 w-6 mr-1.5" />
                    Edit Tablet
                  </h1>
                  <p className="mt-2 text-sm text-gray-700">
                    Update the tablet's details or the location it is locked to.
                  </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                  <button
                    onClick={() => navigate(-1)}
                    type="button"
                    className="inline-flex items-center justify-center rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:w-auto"
                  >
                    <ArrowLeftIcon className="h-5 w-5 mr-1.5" />
                    Back
                  </button>
                </div>
              </div>
              {loading || !device ? (
                <p className="mt-8 text-sm text-gray-700">{loading ? "Loading..." : ""}</p>
              ) : (
                <div className="mt-8 flex flex-col max-w-screen-md">
                  <div className="space-y-8 divide-y divide-gray-200">
                    <div className="space-y-8">
                      <div>
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                          <div className="sm:col-span-4">
                            <label
                              htmlFor="name"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Name
                            </label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                              <input
                                type="text"
                                name="name"
                                id="name"
                                placeholder="MEL-SerialNo"
                                disabled={isBusy}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="flex-1 focus:ring-amber-500 focus:border-amber-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300 disabled:bg-gray-100"
                              />
                            </div>
                          </div>
                          <div className="sm:col-span-4">
                            <label
                              htmlFor="appType"
                              className="block text-sm font-medium text-gray-700"
                            >
                              App
                            </label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                              <select
                                id="appType"
                                name="appType"
                                disabled={isBusy}
                                value={appType}
                                onChange={(e) => updateAppType(e.target.value)}
                                className="flex-1 focus:ring-amber-500 focus:border-amber-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300 disabled:bg-gray-100"
                              >
                                {APP_TYPES.map((type) => (
                                  <option key={type.value} value={type.value}>
                                    {type.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="sm:col-span-4">
                            <label
                              htmlFor="location"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Location
                            </label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                              <select
                                id="location"
                                name="location"
                                disabled={isBusy}
                                value={locationId}
                                onChange={(e) => setLocationId(e.target.value)}
                                className="flex-1 focus:ring-amber-500 focus:border-amber-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300 disabled:bg-gray-100"
                              >
                                <option value="">Select a location</option>
                                {locationOptions.map((location) => (
                                  <option key={location.id} value={location.id}>
                                    {location.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">{locationHint(appType)}</p>
                          </div>
                          <div className="sm:col-span-4">
                            <div className="relative flex items-start">
                              <div className="flex items-center h-5">
                                <input
                                  id="active"
                                  name="active"
                                  type="checkbox"
                                  disabled={isBusy}
                                  checked={active}
                                  onChange={(e) => setActive(e.target.checked)}
                                  className="focus:ring-amber-500 h-4 w-4 text-amber-600 border-gray-300 rounded"
                                />
                              </div>
                              <div className="ml-3 text-sm">
                                <label htmlFor="active" className="font-medium text-gray-700">
                                  Active
                                </label>
                                <p className="text-gray-500">
                                  Inactive tablets are rejected when they check in: kiosks and the
                                  Officer Dashboard both lock themselves out until the tablet is
                                  active again.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="sm:col-span-4 text-sm text-gray-500">
                            <p>
                              Last seen:{" "}
                              {device.lastSeenOn
                                ? new Date(device.lastSeenOn).toLocaleString("en-AU")
                                : "Never"}
                            </p>
                            {(device.brand || device.model) && (
                              <p>
                                Device: {[device.brand, device.model].filter(Boolean).join(" ")}
                                {device.operatingSystem ? ` (${device.operatingSystem})` : ""}
                              </p>
                            )}
                          </div>
                          {device.latitude &&
                            device.longitude &&
                            process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                              <div className="sm:col-span-6">
                                <label className="block text-sm font-medium text-gray-700">
                                  Last Reported Position
                                </label>
                                <div className="mt-1 overflow-hidden rounded-md shadow ring-1 ring-black ring-opacity-5">
                                  <iframe
                                    title="Tablet position"
                                    width="100%"
                                    height="320"
                                    style={{ border: 0, display: "block" }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${device.latitude},${device.longitude}&zoom=17`}
                                  />
                                </div>
                                <p className="mt-1 text-sm text-gray-500">
                                  Reported by the tablet when it last connected
                                  {device.lastSeenOn
                                    ? ` (${new Date(device.lastSeenOn).toLocaleString("en-AU")})`
                                    : ""}
                                  .
                                </p>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="pt-5">
                      <div className="flex justify-start items-center">
                        <button
                          onClick={() => navigate("/v3/tablets")}
                          type="button"
                          disabled={isBusy}
                          className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          onClick={() => updateTablet()}
                          disabled={isBusy || !name || !locationId}
                          className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-500 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-60"
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          onClick={() => setOpenDeleteTabletModal(true)}
                          disabled={isBusy}
                          className="ml-3 inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-60"
                        >
                          <TrashIcon className="h-5 w-5 mr-1.5" />
                          Delete
                        </button>
                        {(!name || !locationId) && (
                          <div className="ml-3 text-red-500 text-sm font-semibold border-none">
                            Please enter all the required fields to update the tablet
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
